import React, { useState, useEffect, useCallback, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api'
import { 
  faFilePdf, 
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faExclamationTriangle,
  faArrowLeft,
  faDownload,
  faEye,
  faCrop,
  faCheck,
  faTimes,
  faArrowRight,
  faArrowLeft as faArrowLeftIcon,
  faSyncAlt,
  faEdit,
  faBug,
  faUpload,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import './PartitionReview.css'

const PartitionReview = () => {
  const [uploadResults, setUploadResults] = useState(null)
  const [formData, setFormData] = useState(null)
  const [questionPapers, setQuestionPapers] = useState([])
  const [currentPaperIndex, setCurrentPaperIndex] = useState(0)
  const [questions, setQuestions] = useState([])
  const [questionReviews, setQuestionReviews] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [showSelectionModal, setShowSelectionModal] = useState(false)
  const [showImageUploadModal, setShowImageUploadModal] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [originalPdfUrl, setOriginalPdfUrl] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [questionPdfUrls, setQuestionPdfUrls] = useState({})
  const [loadingPdfs, setLoadingPdfs] = useState({})
  const [pdfLoadAttempts, setPdfLoadAttempts] = useState({})
  const [showMarksInputModal, setShowMarksInputModal] = useState(false)
  const [marksInput, setMarksInput] = useState('')
  const [questionForMarksUpdate, setQuestionForMarksUpdate] = useState(null)
  const [showPaperPreviewModal, setShowPaperPreviewModal] = useState(false)
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null)
  
  // Sequential review state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [reviewMode, setReviewMode] = useState('sequential') // 'sequential' or 'all'
  const [checkingForQuestions, setCheckingForQuestions] = useState(false)

  // Load data from sessionStorage on component mount
  useEffect(() => {
    const storedResults = sessionStorage.getItem('uploadResults')
    const storedFormData = sessionStorage.getItem('formData')
    
    console.log('Session storage data:', { storedResults, storedFormData })
    
    if (storedResults && storedFormData) {
      try {
        const parsedResults = JSON.parse(storedResults)
        const parsedFormData = JSON.parse(storedFormData)
        
        console.log('Parsed data:', { parsedResults, parsedFormData })
        
        setUploadResults(parsedResults)
        setFormData(parsedFormData)
        
        // Only load question papers after formData is set
        if (parsedFormData && parsedFormData.courseId) {
          loadQuestionPapers(parsedResults, parsedFormData)
        } else {
          console.error('Invalid form data:', parsedFormData)
          setError('Invalid form data found. Please start the upload process again.')
          setIsLoading(false)
        }
      } catch (parseError) {
        console.error('Error parsing session storage data:', parseError)
        setError('Corrupted session data. Please start the upload process again.')
        setIsLoading(false)
      }
    } else {
      console.error('Missing session storage data:', { storedResults, storedFormData })
      
      // Try to get data from URL parameters as fallback
      const urlParams = new URLSearchParams(window.location.search)
      const courseId = urlParams.get('courseId')
      const courseName = urlParams.get('courseName')
      const academicYear = urlParams.get('academicYear')
      const semester = urlParams.get('semester')
      
      if (courseId && courseName && academicYear && semester) {
        console.log('Using URL parameters as fallback:', { courseId, academicYear, semester })
        const fallbackFormData = { courseId, courseName, academicYear, semester }
        setFormData(fallbackFormData)
        
        // Try to load papers with URL parameters
        loadQuestionPapers({ uploaded_files: [] }, fallbackFormData)
      } else {
        setError('No upload data found. Please start the upload process again.')
        setIsLoading(false)
      }
    }
  }, [])


  const loadQuestionPapers = async (results, formDataParam = null) => {
    try {
      setIsLoading(true)
      
      // Use passed formData or state formData
      const currentFormData = formDataParam || formData
      
      if (!currentFormData || !currentFormData.courseId) {
        setError('Form data not available. Please start the upload process again.')
        setIsLoading(false)
        return
      }
      
      // Fetch question papers from the upload results
      const papers = []
      for (const filename of results.uploaded_files || []) {
        // Get paper details from backend using URLSearchParams for proper encoding
        const queryParams = new URLSearchParams({
          course_id: currentFormData.courseId,
          course_name: currentFormData.courseName,
          academic_year: currentFormData.academicYear,
          semester: currentFormData.semester
        });
        const response = await fetch(`${getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPERS)}?${queryParams.toString()}`)
        const data = await response.json()
        
        if (data.papers) {
          const paper = data.papers.find(p => p.original_filename === filename)
          if (paper) {
            papers.push(paper)
          }
        }
      }
      
      setQuestionPapers(papers)
      
      if (papers.length > 0) {
        loadQuestionsForPaper(papers[0].id)
      } else {
        setError('No question papers found. Please check your upload.')
      }
      
    } catch (error) {
      console.error('Error loading question papers:', error)
      setError('Failed to load question papers. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadQuestionsForPaper = async (paperId) => {
    try {
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPER) + `/${paperId}`)
      const data = await response.json()
      
      if (data.questions) {
        setQuestions(data.questions)
        
        // Initialize question reviews
        const reviews = {}
        data.questions.forEach(question => {
          reviews[question.id] = { approved: null, needsCorrection: false }
        })
        setQuestionReviews(reviews)
        
        // Reset to first question
        setCurrentQuestionIndex(0)
        
        // Load PDF for the first question only (sequential mode)
        if (data.questions.length > 0) {
          loadQuestionPdf(data.questions[0].id)
        }
      }
    } catch (error) {
      console.error('Error loading questions:', error)
      setError('Failed to load questions for this paper')
    }
  }

  const loadQuestionPdfs = async (questions, paperId) => {
    console.log(`Loading PDFs for ${questions.length} questions:`, questions.map(q => q.id))
    
    // Clear any existing PDF URLs to ensure fresh loading
    setQuestionPdfUrls({})
    setLoadingPdfs({})
    
    // Load all PDFs in parallel for better performance
    const loadPromises = questions.map(question => loadQuestionPdf(question.id))
    const results = await Promise.allSettled(loadPromises)
    
    // Log results for debugging
    const successful = results.filter(r => r.status === 'fulfilled').length
    const failed = results.filter(r => r.status === 'rejected').length
    console.log(`PDF loading results: ${successful} successful, ${failed} failed`)
    
    // Wait a bit for state to update, then check for failed PDFs
    setTimeout(() => {
      const currentUrls = questionPdfUrls
      const failedQuestions = questions.filter(q => !currentUrls[q.id])
      
      if (failedQuestions.length > 0) {
        console.log(`Retrying ${failedQuestions.length} failed PDFs:`, failedQuestions.map(q => q.id))
        // Retry failed PDFs with exponential backoff
        failedQuestions.forEach((question, index) => {
          setTimeout(() => {
            loadQuestionPdf(question.id, true)
          }, index * 500) // Stagger retries by 500ms each
        })
      } else {
        console.log('All PDFs loaded successfully!')
      }
    }, 1000)
    
    // Final retry after 5 seconds to ensure all PDFs are attempted
    setTimeout(() => {
      const currentUrls = questionPdfUrls
      const stillFailed = questions.filter(q => !currentUrls[q.id])
      
      if (stillFailed.length > 0) {
        console.log(`Final retry for ${stillFailed.length} still failed PDFs:`, stillFailed.map(q => q.id))
        stillFailed.forEach(question => {
          loadQuestionPdf(question.id, true)
        })
      }
    }, 5000)
  }

  const loadQuestionPdf = async (questionId, forceRefresh = false) => {
    try {
      // Track attempts
      setPdfLoadAttempts(prev => ({
        ...prev,
        [questionId]: (prev[questionId] || 0) + 1
      }))
      
      const attempt = pdfLoadAttempts[questionId] || 1
      console.log(`Loading PDF for question: ${questionId} (attempt ${attempt})${forceRefresh ? ' (forced refresh)' : ''}`)
      setLoadingPdfs(prev => ({ ...prev, [questionId]: true }))
      
      // Add cache-busting parameter to force refresh
      const cacheBuster = forceRefresh ? `?t=${Date.now()}` : ''
      const response = await fetch(getApiUrl(SERVERS.ANALYSIS, ENDPOINTS.QUESTION_PDF) + `/${questionId}${cacheBuster}`)
      console.log(`PDF response status for ${questionId}: ${response.status}`)
      
      if (response.ok) {
        const blob = await response.blob()
        console.log(`PDF blob size for ${questionId}: ${blob.size} bytes`)
        
        // Check if blob is valid
        if (blob.size === 0) {
          throw new Error(`PDF blob is empty for question ${questionId}`)
        }
        
        // Create object URL and update state
        const url = URL.createObjectURL(blob)
        
        // If refreshing, clean up old URL
        if (forceRefresh && questionPdfUrls[questionId]) {
          URL.revokeObjectURL(questionPdfUrls[questionId])
        }
        
        setQuestionPdfUrls(prev => {
          const newUrls = { ...prev, [questionId]: url }
          console.log(`Updated PDF URLs:`, Object.keys(newUrls))
          return newUrls
        })
        console.log(`✅ PDF loaded successfully for question ${questionId} on attempt ${attempt}`)
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText} for question ${questionId}`)
      }
    } catch (error) {
      console.error(`❌ Error loading PDF for question ${questionId} on attempt ${pdfLoadAttempts[questionId] || 1}:`, error)
      // Don't remove URL on error, let retry mechanism handle it
    } finally {
      setLoadingPdfs(prev => ({ ...prev, [questionId]: false }))
    }
  }

  const handleQuestionApproval = (questionId, approved) => {
    setQuestionReviews(prev => ({
      ...prev,
      [questionId]: { 
        ...prev[questionId], 
        approved: approved,
        needsCorrection: !approved
      }
    }))
    
  }

  const handleRemoveQuestion = async (question) => {
    if (!question || !question.id) {
      console.error('Invalid question object for removal')
      return
    }

    // Confirm deletion
    const confirmDelete = window.confirm(
      `Are you sure you want to remove Question ${question.question_number}? This action cannot be undone.`
    )
    
    if (!confirmDelete) {
      return
    }

    setIsProcessing(true)
    setError('')

    try {
      console.log('Removing question:', question.id)
      
      // Call backend to delete the question
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.DELETE_QUESTION) + `/${question.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to delete question: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('Question deleted successfully:', result)
      console.log(`Removed ${result.removed_marks || 0} marks. Updated total marks: ${result.updated_total_marks}`)

      // Update local state
      setQuestions(prev => prev.filter(q => q.id !== question.id))
      
      // Remove from question reviews
      setQuestionReviews(prev => {
        const newReviews = { ...prev }
        delete newReviews[question.id]
        return newReviews
      })

      // Clean up PDF URL if it exists
      if (questionPdfUrls[question.id]) {
        URL.revokeObjectURL(questionPdfUrls[question.id])
        setQuestionPdfUrls(prev => {
          const newUrls = { ...prev }
          delete newUrls[question.id]
          return newUrls
        })
      }

      // Adjust current question index if needed
      if (currentQuestionIndex >= questions.length - 1) {
        setCurrentQuestionIndex(Math.max(0, questions.length - 2))
      }

      // Load PDF for the new current question if available
      const newCurrentIndex = Math.max(0, questions.length - 2)
      if (questions[newCurrentIndex]) {
        loadQuestionPdf(questions[newCurrentIndex].id)
      }

      console.log(`Question ${question.question_number} removed successfully`)
      
      // Show success message with marks info
      const removedMarks = result.removed_marks || 0
      if (removedMarks > 0) {
        console.log(`✅ Removed Question ${question.question_number} (${removedMarks} marks)`)
      } else {
        console.log(`✅ Removed Question ${question.question_number}`)
      }
      
    } catch (error) {
      console.error('Error removing question:', error)
      setError(`Failed to remove question: ${error.message}`)
    } finally {
      setIsProcessing(false)
    }
  }

  const nextQuestion = async () => {
    // Check if we're at the last known question
    if (currentQuestionIndex >= questions.length - 1) {
      // Try to load more questions from backend
      await checkForNewQuestions()
      return
    }
    
    // Move to next question
    setCurrentQuestionIndex(prev => prev + 1)
    // Load PDF for the next question
    const nextQuestion = questions[currentQuestionIndex + 1]
    if (nextQuestion) {
      loadQuestionPdf(nextQuestion.id)
    }
  }

  const checkForNewQuestions = async () => {
    try {
      setCheckingForQuestions(true)
      console.log('Checking for new questions...')
      const currentPaper = questionPapers[currentPaperIndex]
      if (!currentPaper) return
      
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPER) + `/${currentPaper.id}`)
      const data = await response.json()
      
      if (data.questions && data.questions.length > questions.length) {
        console.log(`Found ${data.questions.length - questions.length} new questions!`)
        
        // Update questions list
        setQuestions(data.questions)
        
        // Initialize reviews for new questions
        const newReviews = { ...questionReviews }
        data.questions.forEach(question => {
          if (!newReviews[question.id]) {
            newReviews[question.id] = { approved: null, needsCorrection: false }
          }
        })
        setQuestionReviews(newReviews)
        
        // Move to the next question (which should now be available)
        setCurrentQuestionIndex(prev => prev + 1)
        const nextQuestion = data.questions[currentQuestionIndex + 1]
        if (nextQuestion) {
          loadQuestionPdf(nextQuestion.id)
        }
      } else {
        console.log('No new questions found')
        // Show message that no more questions are available
        setError('No more questions available. You have reached the end of the current questions.')
      }
    } catch (error) {
      console.error('Error checking for new questions:', error)
      setError('Failed to check for new questions')
    } finally {
      setCheckingForQuestions(false)
    }
  }

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    }
  }

  const getCurrentQuestion = () => {
    return questions[currentQuestionIndex] || null
  }

  const isCurrentQuestionComplete = () => {
    const currentQuestion = getCurrentQuestion()
    if (!currentQuestion) return false
    const review = questionReviews[currentQuestion.id]
    return review?.approved !== null
  }

  const getProgressInfo = () => {
    const total = questions.length
    const current = currentQuestionIndex + 1
    const completed = questions.filter(q => questionReviews[q.id]?.approved !== null).length
    return { current, total, completed }
  }

  const handleQuestionCorrection = (question) => {
    console.log('Handling question correction for:', question)
    console.log('Current paper index:', currentPaperIndex)
    console.log('Current paper:', questionPapers[currentPaperIndex])
    
    // Show marks input modal first
    setQuestionForMarksUpdate(question)
    setMarksInput(question.marks ? question.marks.toString() : '')
    setShowMarksInputModal(true)
  }

  const handleMarksInputSubmit = async () => {
    if (!questionForMarksUpdate) return

    const marks = parseFloat(marksInput)
    if (isNaN(marks) || marks < 0) {
      setError('Please enter a valid marks value (0 or greater)')
      return
    }

    setIsProcessing(true)
    setError('')

    try {
      // Update question marks in backend
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.UPDATE_QUESTION_MARKS) + `/${questionForMarksUpdate.id}/marks`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          marks: marks,
          old_marks: questionForMarksUpdate.marks || 0
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Failed to update question marks: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('Question marks updated successfully:', result)

      // Update local state
      setQuestions(prev => prev.map(q => 
        q.id === questionForMarksUpdate.id 
          ? { ...q, marks: marks }
          : q
      ))

      // Close marks modal and proceed with correction
      setShowMarksInputModal(false)
      setQuestionForMarksUpdate(null)
      setMarksInput('')

      // Now proceed with the original correction flow
      setSelectedQuestion(questionForMarksUpdate)
      setShowImageUploadModal(true)
      loadOriginalPdf(questionForMarksUpdate)

    } catch (error) {
      console.error('Error updating question marks:', error)
      setError(`Failed to update question marks: ${error.message}`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleMarksInputCancel = () => {
    setShowMarksInputModal(false)
    setQuestionForMarksUpdate(null)
    setMarksInput('')
    setError('')
  }

  const handlePaperPreview = async () => {
    try {
      setIsProcessing(true)
      setError('')
      
      // Get current paper ID
      const paperId = questionPapers[currentPaperIndex]?.id
      
      if (!paperId) {
        setError('Unable to find paper ID')
        return
      }
      
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPER) + `/${paperId}/download`)
      
      if (response.ok) {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setPreviewPdfUrl(url)
        setShowPaperPreviewModal(true)
      } else {
        setError('Failed to load question paper preview')
      }
    } catch (error) {
      console.error('Error loading paper preview:', error)
      setError('Failed to load question paper preview')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClosePaperPreview = () => {
    setShowPaperPreviewModal(false)
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl)
      setPreviewPdfUrl(null)
    }
  }

  const handleImageUpload = async (formData) => {
    if (!selectedQuestion) return
    
    setIsProcessing(true)
    try {
      // Send images to backend for PDF creation
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, 'CREATE_PDF_FROM_IMAGES'), {
        method: 'POST',
        body: formData
      })
      
      if (response.ok) {
        const result = await response.json()
        console.log('PDF created from images:', result)
        
        // Update question review status
        setQuestionReviews(prev => ({
          ...prev,
          [selectedQuestion.id]: { 
            ...prev[selectedQuestion.id], 
            approved: true,
            needsCorrection: false,
            corrected: true
          }
        }))
        
        setShowImageUploadModal(false)
        setSelectedQuestion(null)
        setOriginalPdfUrl(null)
        setError('')
      } else {
        const errorData = await response.json()
        setError(errorData.detail || 'Failed to create PDF from images')
      }
    } catch (error) {
      console.error('Error creating PDF from images:', error)
      setError('Failed to create PDF from images')
    } finally {
      setIsProcessing(false)
    }
  }

  const loadOriginalPdf = async (question) => {
    try {
      console.log('Loading original PDF for question:', question)
      
      // Get the paper ID from the question - try multiple possible property names
      let paperId = question.question_paper_id || question.paper_id || question.question_paper?.id
      
      // Fallback to current paper ID if question doesn't have it
      if (!paperId && questionPapers[currentPaperIndex]) {
        paperId = questionPapers[currentPaperIndex].id
        console.log('Using current paper ID as fallback:', paperId)
      }
      
      console.log('Paper ID found:', paperId)
      
      if (!paperId) {
        console.error('No paper ID found in question object or current paper')
        setError('Unable to find paper ID for this question')
        return
      }
      
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPER) + `/${paperId}/download`)
      console.log('Original PDF response status:', response.status)
      
      if (response.ok) {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setOriginalPdfUrl(url)
        console.log('Original PDF loaded successfully')
      } else {
        console.error('Failed to load original PDF:', response.status, response.statusText)
        setError('Failed to load original PDF')
      }
    } catch (error) {
      console.error('Error loading original PDF:', error)
      setError('Failed to load original PDF')
    }
  }

  const handleCoordinateSelection = async (coordinates) => {
    if (!selectedQuestion) return
    
    setIsProcessing(true)
    try {
      // Send coordinates to backend for PDF cropping
      const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.CROP_QUESTION), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question_id: selectedQuestion.id,
          coordinates: coordinates
        })
      })
      
      if (response.ok) {
        // Update question review status
        setQuestionReviews(prev => ({
          ...prev,
          [selectedQuestion.id]: { 
            ...prev[selectedQuestion.id], 
            approved: true,
            needsCorrection: false,
            corrected: true
          }
        }))
        
        setShowSelectionModal(false)
        setSelectedQuestion(null)
        setOriginalPdfUrl(null)
      } else {
        setError('Failed to update question partition')
      }
    } catch (error) {
      console.error('Error updating question:', error)
      setError('Failed to update question partition')
    } finally {
      setIsProcessing(false)
    }
  }

  const nextPaper = () => {
    if (currentPaperIndex < questionPapers.length - 1) {
      const nextIndex = currentPaperIndex + 1
      setCurrentPaperIndex(nextIndex)
      loadQuestionsForPaper(questionPapers[nextIndex].id)
    }
  }

  const prevPaper = () => {
    if (currentPaperIndex > 0) {
      const prevIndex = currentPaperIndex - 1
      setCurrentPaperIndex(prevIndex)
      loadQuestionsForPaper(questionPapers[prevIndex].id)
    }
  }

  const isCurrentPaperComplete = () => {
    return questions.every(question => 
      questionReviews[question.id]?.approved !== null
    )
  }

  const isAllPapersComplete = () => {
    return questionPapers.every((paper, index) => {
      if (index === currentPaperIndex) {
        return isCurrentPaperComplete()
      }
      // For other papers, we'd need to check their completion status
      return true // Simplified for now
    })
  }

  const handleComplete = () => {
    // Clear session storage and redirect to upload page
    sessionStorage.removeItem('uploadResults')
    sessionStorage.removeItem('formData')
    window.location.href = '/upload_page'
  }

  if (isLoading) {
    return (
      <div className="partition-review">
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} className="spinning large-spinner" />
          <h2>Loading Partition Review</h2>
          <p>Please wait while we load your question papers...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="partition-review">
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <h2>Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/upload_page'}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Upload
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              <FontAwesomeIcon icon={faSpinner} />
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="partition-review">
      <div className="container">
        {/* Header */}
        <div className="review-header">
          <div className="header-content">
            <div className="header-icon">
              <FontAwesomeIcon icon={faFilePdf} />
            </div>
            <div className="header-text">
              <h1>Partition Review</h1>
              <p>Review and approve question partitions for your uploaded papers</p>
            </div>
          </div>
          <div className="progress-indicator">
            <span>Paper {currentPaperIndex + 1} of {questionPapers.length}</span>
          </div>
        </div>

        {/* Paper Navigation */}
        {questionPapers.length > 1 && (
          <div className="paper-navigation">
            <button 
              className="btn btn-secondary"
              onClick={prevPaper}
              disabled={currentPaperIndex === 0}
            >
              <FontAwesomeIcon icon={faArrowLeftIcon} />
              Previous Paper
            </button>
            <div className="paper-info">
              <h3>{questionPapers[currentPaperIndex]?.original_filename}</h3>
              <p>{questionPapers[currentPaperIndex]?.course_name} • {questionPapers[currentPaperIndex]?.instructor_name}</p>
            </div>
            <button 
              className="btn btn-secondary"
              onClick={nextPaper}
              disabled={currentPaperIndex === questionPapers.length - 1}
            >
              Next Paper
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        )}

         {/* Questions Review */}
         <div className="questions-section">
           <div className="review-header-section">
             <h2>Question Partitions</h2>
             <p>Review each question partition and approve or correct as needed.</p>
             
             {/* Progress Indicator */}
             <div className="progress-section">
               <div className="progress-bar">
                 <div 
                   className="progress-fill" 
                   style={{ 
                     width: `${(getProgressInfo().completed / getProgressInfo().total) * 100}%` 
                   }}
                 />
               </div>
               <div className="progress-text">
                 Question {getProgressInfo().current} of {getProgressInfo().total} 
                 ({getProgressInfo().completed} completed)
               </div>
             </div>
             
           </div>
           
           {/* Current Question Display */}
           {getCurrentQuestion() && (
             <div className="current-question">
               <div className="question-card">
                 <div className="question-header">
                   <h3>Question {getCurrentQuestion().question_number}</h3>
                   <div className="question-meta">
                     <span className="marks">{getCurrentQuestion().marks} marks</span>
                     <span className="file-size">{getCurrentQuestion().file_size_mb} MB</span>
                   </div>
                 </div>
                 
                 <div className="question-pdf-viewer">
                   <h5>
                     <FontAwesomeIcon icon={faFilePdf} />
                     Question PDF:
                   </h5>
                   {loadingPdfs[getCurrentQuestion().id] ? (
                     <div className="pdf-loading">
                       <FontAwesomeIcon icon={faSpinner} spin />
                       Loading PDF...
                     </div>
                   ) : questionPdfUrls[getCurrentQuestion().id] ? (
                     <div className="pdf-container">
                       <object
                         data={questionPdfUrls[getCurrentQuestion().id]}
                         type="application/pdf"
                         width="100%"
                         height="500px"
                         style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                       >
                         <div style={{ padding: '20px', textAlign: 'center' }}>
                           <p style={{ marginBottom: '10px' }}>PDF viewer not available in your browser.</p>
                           <p style={{ marginBottom: '15px' }}>Please use the "Refresh PDF" button above or download the PDF to view it.</p>
                           <a href={questionPdfUrls[getCurrentQuestion().id]} download={`question_${getCurrentQuestion().question_number}.pdf`} 
                              style={{ color: '#007bff', textDecoration: 'underline' }}>
                             Download PDF
                           </a>
                         </div>
                       </object>
                       <div className="pdf-actions">
                         <button
                           className="btn btn-secondary btn-sm"
                           onClick={() => loadQuestionPdf(getCurrentQuestion().id, true)}
                           disabled={loadingPdfs[getCurrentQuestion().id]}
                         >
                           <FontAwesomeIcon icon={faSyncAlt} spin={loadingPdfs[getCurrentQuestion().id]} />
                           Refresh PDF
                         </button>
                         <a 
                           href={questionPdfUrls[getCurrentQuestion().id]} 
                           download={`question_${getCurrentQuestion().question_number}.pdf`}
                           className="btn btn-secondary btn-sm"
                         >
                           <FontAwesomeIcon icon={faDownload} />
                           Download PDF
                         </a>
                       </div>
                     </div>
                   ) : (
                     <div className="pdf-error">
                       <FontAwesomeIcon icon={faExclamationTriangle} />
                       Failed to load PDF
                       <button
                         className="btn btn-primary btn-sm"
                         onClick={() => loadQuestionPdf(getCurrentQuestion().id, true)}
                         style={{ marginTop: '1rem' }}
                       >
                         <FontAwesomeIcon icon={faSyncAlt} />
                         Retry
                       </button>
                     </div>
                   )}
                 </div>
                 
                 <div className="question-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                   <div style={{ display: 'flex', gap: '0.5rem' }}>
                     {!isCurrentQuestionComplete() && (
                       <>
                         <button
                           className="btn btn-success"
                           onClick={() => handleQuestionApproval(getCurrentQuestion().id, true)}
                         >
                           <FontAwesomeIcon icon={faCheckCircle} />
                           Approve Partition
                         </button>
                         <button
                           className="btn btn-warning"
                           onClick={() => handleQuestionCorrection(getCurrentQuestion())}
                         >
                           <FontAwesomeIcon icon={faCrop} />
                           Adjust Partition
                         </button>
                         <button
                           className="btn btn-danger"
                           onClick={() => handleRemoveQuestion(getCurrentQuestion())}
                         >
                           <FontAwesomeIcon icon={faTimes} />
                           Remove Question
                         </button>
                       </>
                     )}
                   </div>
                   
                   <button
                     className="btn btn-info"
                     onClick={handlePaperPreview}
                     disabled={isProcessing}
                   >
                     <FontAwesomeIcon icon={faEye} />
                     Question Paper Preview
                   </button>
                 </div>
                 
                 {isCurrentQuestionComplete() && (
                   <div className="completed-status">
                     <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
                     <span>Question Reviewed</span>
                   </div>
                 )}
               </div>
               
               {/* Navigation Controls */}
               <div className="question-navigation">
                 <button 
                   className="btn btn-secondary"
                   onClick={prevQuestion}
                   disabled={currentQuestionIndex === 0}
                 >
                   <FontAwesomeIcon icon={faArrowLeft} />
                   Previous
                 </button>
                 
                 <div className="nav-info">
                   <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
                   {currentQuestionIndex >= questions.length - 1 && (
                     <div className="check-more-questions">
                       <button 
                         className="btn btn-outline btn-sm"
                         onClick={checkForNewQuestions}
                         disabled={checkingForQuestions}
                         style={{ marginTop: '0.5rem' }}
                       >
                         <FontAwesomeIcon 
                           icon={faSyncAlt} 
                           spin={checkingForQuestions}
                         />
                         {checkingForQuestions ? 'Checking...' : 'Check for More Questions'}
                       </button>
                     </div>
                   )}
                 </div>
                 
                 <button 
                   className="btn btn-primary"
                   onClick={nextQuestion}
                   disabled={currentQuestionIndex === questions.length - 1}
                 >
                   Next
                   <FontAwesomeIcon icon={faArrowRight} />
                 </button>
               </div>
             </div>
           )}
         </div>

        {/* Completion Actions */}
        <div className="completion-section">
          {isCurrentPaperComplete() && currentPaperIndex < questionPapers.length - 1 && (
            <div className="paper-complete">
              <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
              <h3>Paper {currentPaperIndex + 1} Complete!</h3>
              <p>All questions have been reviewed. Moving to next paper...</p>
              <button className="btn btn-primary" onClick={nextPaper}>
                Next Paper
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          )}
          
          {isAllPapersComplete() && (
            <div className="all-complete">
              <FontAwesomeIcon icon={faCheckCircle} className="success-icon large" />
              <h2>All Papers Complete!</h2>
              <p>All question partitions have been reviewed and approved.</p>
              <button className="btn btn-success btn-large" onClick={handleComplete}>
                <FontAwesomeIcon icon={faCheck} />
                Finish Review
              </button>
            </div>
          )}
        </div>
      </div>

       {/* Marks Input Modal */}
       {showMarksInputModal && (
         <MarksInputModal
           question={questionForMarksUpdate}
           marksInput={marksInput}
           setMarksInput={setMarksInput}
           onSubmit={handleMarksInputSubmit}
           onCancel={handleMarksInputCancel}
           isProcessing={isProcessing}
           error={error}
         />
       )}

       {/* Image Upload Modal */}
       {showImageUploadModal && (
         <ImageUploadModal
           question={selectedQuestion}
           originalPdfUrl={originalPdfUrl}
           onUpload={handleImageUpload}
           onClose={() => {
             setShowImageUploadModal(false)
             setSelectedQuestion(null)
             setOriginalPdfUrl(null)
             setError('') // Clear error when closing modal
           }}
           isProcessing={isProcessing}
           error={error}
           onRetry={() => loadOriginalPdf(selectedQuestion)}
         />
       )}

       {/* Question Paper Preview Modal */}
       {showPaperPreviewModal && (
         <PaperPreviewModal
           pdfUrl={previewPdfUrl}
           onClose={handleClosePaperPreview}
           paperInfo={questionPapers[currentPaperIndex]}
         />
       )}
    </div>
  )
}

// Marks Input Modal Component
const MarksInputModal = ({ question, marksInput, setMarksInput, onSubmit, onCancel, isProcessing, error }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content marks-input-modal">
        <div className="modal-header">
          <h3>Update Question Marks</h3>
          <button className="close-btn" onClick={onCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="marks-input-section">
            <h4>Question {question?.question_number}</h4>
            <p>Please enter the marks allocated for this question:</p>
            
            <div className="form-group">
              <label htmlFor="marks-input">Marks:</label>
              <input
                id="marks-input"
                type="number"
                value={marksInput}
                onChange={(e) => setMarksInput(e.target.value)}
                placeholder="Enter marks (e.g., 10, 15.5)"
                min="0"
                step="0.5"
                className="marks-input-field"
                disabled={isProcessing}
              />
              <small className="form-help">
                Enter the marks allocated for this question. This will update the total marks for the question paper.
              </small>
            </div>

            {error && (
              <div className="error-message">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel} disabled={isProcessing}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={isProcessing || !marksInput || parseFloat(marksInput) < 0}
          >
            {isProcessing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="spinning" />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} />
                Update Marks & Continue
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Image Upload Modal Component
const ImageUploadModal = ({ question, originalPdfUrl, onUpload, onClose, isProcessing, error, onRetry }) => {
  const [uploadedImages, setUploadedImages] = useState([])
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files)
    }
  }

  const handleFiles = (files) => {
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    )
    
    if (imageFiles.length === 0) {
      alert('Please select image files only')
      return
    }

    const newImages = imageFiles.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file)
    }))

    setUploadedImages(prev => [...prev, ...newImages])
  }

  const removeImage = (imageId) => {
    setUploadedImages(prev => {
      const image = prev.find(img => img.id === imageId)
      if (image) {
        URL.revokeObjectURL(image.preview)
      }
      return prev.filter(img => img.id !== imageId)
    })
  }

  const handleUpload = async () => {
    if (uploadedImages.length === 0) {
      alert('Please select at least one image')
      return
    }

    const formData = new FormData()
    formData.append('question_id', question.id)
    
    // Append all image files with the key 'files' as expected by the backend
    uploadedImages.forEach((image) => {
      formData.append('files', image.file)
    })

    onUpload(formData)
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content image-upload-modal">
        <div className="modal-header">
          <h3>Upload Question Images</h3>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="upload-instructions">
            <h4>Instructions:</h4>
            <ol>
              <li>Take screenshots/snips of the correct question parts from the PDF below</li>
              <li>Upload the images using the area below</li>
              <li>For large questions, you can upload multiple images</li>
              <li>Images will be combined into a single PDF in the order you upload them</li>
            </ol>
          </div>

          {/* Original PDF Display */}
          <div className="original-pdf-section">
            <h5>Original PDF (for reference):</h5>
            {originalPdfUrl ? (
              <iframe
                src={originalPdfUrl}
                width="100%"
                height="400px"
                style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                title="Original PDF for reference"
              />
            ) : (
              <div className="pdf-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                Loading PDF...
              </div>
            )}
          </div>

          {/* Image Upload Area */}
          <div className="image-upload-section">
            <h5>Upload Images:</h5>
            <div 
              className={`image-dropzone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
              <div className="dropzone-content">
                <FontAwesomeIcon icon={faUpload} size="2x" style={{ color: '#000000' }} />
                <p style={{ color: '#000000', fontWeight: '600' }}>Drag & drop images here or click to select</p>
                <p className="dropzone-hint" style={{ color: '#000000', fontWeight: '500' }}>Supports multiple images for large questions</p>
              </div>
            </div>

            {/* Uploaded Images Preview */}
            {uploadedImages.length > 0 && (
              <div className="uploaded-images">
                <h6>Uploaded Images ({uploadedImages.length}):</h6>
                <div className="images-grid">
                  {uploadedImages.map((image, index) => (
                    <div key={image.id} className="image-preview">
                      <img src={image.preview} alt={`Upload ${index + 1}`} />
                      <div className="image-info">
                        <span className="image-name">{image.name}</span>
                        <span className="image-size">{(image.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <button 
                        className="remove-image-btn"
                        onClick={() => removeImage(image.id)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
              <button onClick={onRetry} className="retry-btn">
                <FontAwesomeIcon icon={faSyncAlt} />
                Retry
              </button>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={uploadedImages.length === 0 || isProcessing}
          >
            {isProcessing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="spinning" />
                Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} />
                Upload & Create PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// PDF Selection Modal Component (keeping for backward compatibility)
const PdfSelectionModal = ({ question, originalPdfUrl, onSelect, onClose, isProcessing, error, onRetry }) => {
  const [coordinates, setCoordinates] = useState(null)
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false) // false = scroll mode, true = selection mode

  const handleMouseDown = (e) => {
    if (!selectionMode) return
    
    e.preventDefault()
    e.stopPropagation()
    setIsSelecting(true)
    const rect = e.currentTarget.getBoundingClientRect()
    const startX = e.clientX - rect.left
    const startY = e.clientY - rect.top
    setCoordinates({ startX, startY, endX: startX, endY: startY })
    console.log('Mouse down - starting selection at:', { startX, startY })
  }

  const handleMouseMove = (e) => {
    if (!isSelecting || !selectionMode) return
    
    e.preventDefault()
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    const endX = e.clientX - rect.left
    const endY = e.clientY - rect.top
    
    setCoordinates(prev => {
      const newCoords = { ...prev, endX, endY }
      console.log('Mouse move - updating selection:', newCoords)
      return newCoords
    })
  }

  const handleMouseUp = (e) => {
    if (!isSelecting || !selectionMode) return
    
    e.preventDefault()
    e.stopPropagation()
    setIsSelecting(false)
    console.log('Mouse up - selection complete:', coordinates)
  }

  const handleDebugCoordinates = async () => {
    if (coordinates && question) {
      // Get the actual iframe dimensions for proper coordinate scaling
      const iframe = document.querySelector('iframe[title*="Original PDF"]')
      let iframeWidth = 800 // Default fallback
      let iframeHeight = 600 // Default fallback
      let pdfDisplayWidth = 0
      let pdfDisplayHeight = 0
      
      if (iframe) {
        const rect = iframe.getBoundingClientRect()
        iframeWidth = rect.width
        iframeHeight = rect.height
        
        // Try to get the actual PDF display dimensions from the iframe
        try {
          // Access the iframe's document to get PDF dimensions
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
          if (iframeDoc) {
            // Look for PDF viewer elements that might contain the actual PDF dimensions
            const pdfViewer = iframeDoc.querySelector('[data-page-number]') || 
                             iframeDoc.querySelector('.page') ||
                             iframeDoc.querySelector('canvas')
            
            if (pdfViewer) {
              const pdfRect = pdfViewer.getBoundingClientRect()
              pdfDisplayWidth = pdfRect.width
              pdfDisplayHeight = pdfRect.height
            }
          }
        } catch (e) {
          console.log('Cannot access iframe content (CORS), using fallback')
        }
        
        console.log('Iframe dimensions:', { width: iframeWidth, height: iframeHeight })
        console.log('PDF display dimensions:', { width: pdfDisplayWidth, height: pdfDisplayHeight })
      }
      
      // Include iframe dimensions and PDF display dimensions in coordinates for backend scaling
      const coordinatesWithDimensions = {
        ...coordinates,
        iframeWidth,
        iframeHeight,
        pdfDisplayWidth: pdfDisplayWidth || iframeWidth, // Use iframe width as fallback
        pdfDisplayHeight: pdfDisplayHeight || iframeHeight // Use iframe height as fallback
      }
      
      try {
        const response = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.DEBUG_COORDINATES), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            question_id: question.id,
            coordinates: coordinatesWithDimensions
          })
        })
        
        if (response.ok) {
          const debugInfo = await response.json()
          console.log('Debug coordinate conversion:', debugInfo)
          alert(`Debug Info:\nPDF: ${debugInfo.pdf_dimensions.width}x${debugInfo.pdf_dimensions.height}\nIframe: ${debugInfo.iframe_dimensions.width}x${debugInfo.iframe_dimensions.height}\nPDF Display: ${debugInfo.pdf_display_dimensions?.width || 'N/A'}x${debugInfo.pdf_display_dimensions?.height || 'N/A'}\nScale: x=${debugInfo.scale_factors.x.toFixed(3)}, y=${debugInfo.scale_factors.y.toFixed(3)}\nSelection: ${debugInfo.selection_area.width.toFixed(1)}x${debugInfo.selection_area.height.toFixed(1)} points`)
        } else {
          console.error('Debug request failed:', response.status)
        }
      } catch (error) {
        console.error('Debug request error:', error)
      }
    }
  }

  const handleConfirm = () => {
    if (coordinates) {
      // Get the actual iframe dimensions for proper coordinate scaling
      const iframe = document.querySelector('iframe[title*="Original PDF"]')
      let iframeWidth = 800 // Default fallback
      let iframeHeight = 600 // Default fallback
      let pdfDisplayWidth = 0
      let pdfDisplayHeight = 0
      
      if (iframe) {
        const rect = iframe.getBoundingClientRect()
        iframeWidth = rect.width
        iframeHeight = rect.height
        
        // Try to get the actual PDF display dimensions from the iframe
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
          if (iframeDoc) {
            const pdfViewer = iframeDoc.querySelector('[data-page-number]') || 
                             iframeDoc.querySelector('.page') ||
                             iframeDoc.querySelector('canvas')
            
            if (pdfViewer) {
              const pdfRect = pdfViewer.getBoundingClientRect()
              pdfDisplayWidth = pdfRect.width
              pdfDisplayHeight = pdfRect.height
            }
          }
        } catch (e) {
          console.log('Cannot access iframe content (CORS), using fallback')
        }
        
        console.log('Iframe dimensions:', { width: iframeWidth, height: iframeHeight })
        console.log('PDF display dimensions:', { width: pdfDisplayWidth, height: pdfDisplayHeight })
      }
      
      // Include iframe dimensions and PDF display dimensions in coordinates for backend scaling
      const coordinatesWithDimensions = {
        ...coordinates,
        iframeWidth,
        iframeHeight,
        pdfDisplayWidth: pdfDisplayWidth || iframeWidth, // Use iframe width as fallback
        pdfDisplayHeight: pdfDisplayHeight || iframeHeight // Use iframe height as fallback
      }
      
      onSelect(coordinatesWithDimensions)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Select Correct Question Region</h3>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="mode-controls">
            <div className="mode-buttons">
              <button 
                className={`btn btn-sm ${!selectionMode ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSelectionMode(false)}
              >
                <FontAwesomeIcon icon={faEye} />
                Scroll Mode
              </button>
              <button 
                className={`btn btn-sm ${selectionMode ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSelectionMode(true)}
              >
                <FontAwesomeIcon icon={faCrop} />
                Select Mode
              </button>
            </div>
            <div className="mode-instructions">
              {selectionMode ? (
                <p><strong>Selection Mode:</strong> Click and drag to select the correct question region</p>
              ) : (
                <p><strong>Scroll Mode:</strong> Scroll to navigate through the PDF, then switch to Select Mode to choose the region</p>
              )}
            </div>
          </div>
          
          <div className="pdf-container">
             {originalPdfUrl ? (
               <div className="pdf-selection-container">
                 <div 
                   className={`pdf-selection-area ${isSelecting ? 'selecting' : ''}`}
                   style={{ 
                     position: 'relative', 
                     border: '2px dashed #007bff',
                     borderRadius: '8px',
                     padding: '10px',
                     background: isSelecting ? 'rgba(0, 123, 255, 0.1)' : 'rgba(0, 123, 255, 0.05)'
                   }}
                 >
                   <iframe
                     src={originalPdfUrl}
                     width="100%"
                     height="600px"
                     style={{ 
                       border: '1px solid #ddd', 
                       borderRadius: '4px',
                       pointerEvents: 'auto' // Allow scrolling
                     }}
                     title={`Original PDF for Question ${question?.question_number}`}
                   />
                   
                   {/* Invisible overlay for selection when in selection mode */}
                   {selectionMode && (
                     <div
                       className="selection-overlay-invisible"
                       onMouseDown={handleMouseDown}
                       onMouseMove={handleMouseMove}
                       onMouseUp={handleMouseUp}
                       style={{
                         position: 'absolute',
                         top: 0,
                         left: 0,
                         right: 0,
                         bottom: 0,
                         cursor: isSelecting ? 'crosshair' : 'default',
                         pointerEvents: 'auto',
                         zIndex: 5
                       }}
                     />
                   )}
                   
                   {coordinates && (
                     <div 
                       className="selection-overlay"
                       style={{
                         position: 'absolute',
                         left: Math.min(coordinates.startX, coordinates.endX),
                         top: Math.min(coordinates.startY, coordinates.endY),
                         width: Math.abs(coordinates.endX - coordinates.startX),
                         height: Math.abs(coordinates.endY - coordinates.startY),
                         background: 'rgba(0, 123, 255, 0.3)',
                         border: '2px solid #007bff',
                         pointerEvents: 'none',
                         zIndex: 10,
                         borderRadius: '4px'
                       }}
                     />
                   )}
                 </div>
               </div>
             ) : (
               <div className="loading-pdf">
                 <FontAwesomeIcon icon={faSpinner} className="spinning" />
                 <p>Loading original PDF...</p>
                 <p className="loading-hint">Please wait while we prepare the PDF for selection</p>
                 {error && (
                   <div className="pdf-error-message">
                     <FontAwesomeIcon icon={faExclamationTriangle} />
                     <p>{error}</p>
                     <button 
                       className="btn btn-primary btn-sm"
                       onClick={onRetry}
                     >
                       <FontAwesomeIcon icon={faSyncAlt} />
                       Retry
                     </button>
                   </div>
                 )}
               </div>
             )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="btn btn-info"
            onClick={handleDebugCoordinates}
            disabled={!coordinates || isProcessing}
          >
            <FontAwesomeIcon icon={faBug} />
            Debug
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!coordinates || isProcessing}
          >
            {isProcessing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="spinning" />
                Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} />
                Confirm Selection
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Paper Preview Modal Component
const PaperPreviewModal = ({ pdfUrl, onClose, paperInfo }) => {
  return (
    <div className="modal-overlay" style={{ zIndex: 2000 }}>
      <div className="modal-content paper-preview-modal" style={{ maxWidth: '90vw', maxHeight: '90vh', width: '90vw', height: '90vh' }}>
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={faEye} style={{ marginRight: '8px' }} />
            Question Paper Preview
          </h3>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-body" style={{ padding: '0', height: 'calc(100% - 60px)', overflow: 'hidden' }}>
          {paperInfo && (
            <div style={{ 
              padding: '10px 20px', 
              background: '#f8f9fa', 
              borderBottom: '1px solid #dee2e6',
              fontSize: '14px'
            }}>
              <strong>Course:</strong> {paperInfo.course_name} | 
              <strong style={{ marginLeft: '15px' }}>Academic Year:</strong> {paperInfo.academic_year} | 
              <strong style={{ marginLeft: '15px' }}>Semester:</strong> {paperInfo.semester}
            </div>
          )}
          
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: paperInfo ? 'calc(100% - 42px)' : '100%',
                border: 'none'
              }}
              title="Question Paper Preview"
            />
          ) : (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: '15px'
            }}>
              <FontAwesomeIcon icon={faSpinner} className="spinning" size="2x" />
              <p>Loading question paper preview...</p>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
            Close Preview
          </button>
        </div>
      </div>
    </div>
  )
}

export default PartitionReview
